import fs from 'node:fs';
import path from 'node:path';
import { loadBootstrapState } from '../utils/bootstrap-state.js';

export type CliDirectoryContext =
  | 'outside_project'
  | 'inside_project_unlinked'
  | 'inside_project_linked';

export interface HomeAction {
  id: 'init' | 'login' | 'deploy' | 'exit';
  label: string;
  description: string;
  argv: string[] | null;
}

const PROJECT_SIGNAL_PATHS = [
  '.agents',
  'agents',
  'vite.config.ts',
  'vite.config.js',
  'vite.config.mts',
  'vite.config.mjs',
] as const;

function hasProjectSignals(cwd: string): boolean {
  const hits = PROJECT_SIGNAL_PATHS.filter((signal) => fs.existsSync(path.join(cwd, signal))).length;
  return hits >= 2 || fs.existsSync(path.join(cwd, '.agents'));
}

export function detectCliDirectoryContext(cwd: string = process.cwd()): CliDirectoryContext {
  if (!hasProjectSignals(cwd)) {
    return 'outside_project';
  }

  const bootstrapState = loadBootstrapState(cwd);
  if (bootstrapState && bootstrapState.account?.id && bootstrapState.user?.id) {
    return 'inside_project_linked';
  }

  return 'inside_project_unlinked';
}

export function getContextLabel(context: CliDirectoryContext): string {
  switch (context) {
    case 'outside_project':
      return 'Outside project';
    case 'inside_project_unlinked':
      return 'Project detected (unlinked)';
    case 'inside_project_linked':
      return 'Linked StandardAgents project';
    default:
      return 'Unknown context';
  }
}

export function getHomeActions(context: CliDirectoryContext): HomeAction[] {
  const shared: HomeAction[] = [
    {
      id: 'exit',
      label: 'Exit',
      description: 'Close this menu.',
      argv: null,
    },
  ];

  if (context === 'outside_project') {
    return [
      {
        id: 'init',
        label: 'Init new project',
        description: 'Create a new Standard Agents project in a fresh directory.',
        argv: ['init'],
      },
      ...shared,
    ];
  }

  if (context === 'inside_project_unlinked') {
    return [
      {
        id: 'login',
        label: 'Link this project',
        description: 'Authenticate and link this local project to a StandardAgents org.',
        argv: ['login'],
      },
      ...shared,
    ];
  }

  return [
    {
      id: 'deploy',
      label: 'Deploy',
      description: 'Push this linked project to StandardAgents for a platform build.',
      argv: ['deploy'],
    },
    {
      id: 'login',
      label: 'Switch or refresh project link',
      description: 'Change org context for this project or refresh its local auth link.',
      argv: ['login'],
    },
    ...shared,
  ];
}
